import { apiWithAuth } from "@/lib/axios";
import type { Skill } from "./types";
import { withFollow } from "./followHelpers";

export const listAlertsSkill: Skill = {
  id: "listAlerts",
  description:
    "Lista alertas operativas del restaurante (stock bajo, sin stock, etc.).",
  schema: {
    type: "object",
    properties: {
      restaurantId: { type: "string" },
      unreadOnly: { type: "boolean", description: "Solo alertas no leídas" },
    },
    required: ["restaurantId"],
  },
  async run(args, ctx) {
    if (!ctx.accessToken) return { ok: false, error: "Sesión no disponible." };
    const restaurantId = String(args.restaurantId ?? "");
    if (!restaurantId) return { ok: false, error: "Falta restaurantId." };
    return withFollow(ctx, {
      intro: "Consultando alertas…",
      successMsg: "✓ Alertas obtenidas",
      run: async () => {
        try {
          const params = new URLSearchParams();
          if (args.unreadOnly) params.set("unreadOnly", "true");
          const { data } = await apiWithAuth.get(
            `/restaurants/${restaurantId}/alerts?${params.toString()}`,
            ctx.accessToken,
          );
          const items: any[] = data?.items ?? data?.data ?? data ?? [];
          return {
            ok: true as const,
            result: {
              count: items.length,
              alerts: items.slice(0, 30).map((a) => ({
                id: a.id,
                type: a.type,
                productName: a.productName,
                stock: a.stock,
                createdAt: a.createdAt,
                isRead: a.isRead,
              })),
            },
            humanSummary: `${items.length} alerta(s).`,
          };
        } catch (err: any) {
          return { ok: false as const, error: err?.response?.data?.message ?? "Error." };
        }
      },
    });
  },
};

export const markAlertReadSkill: Skill = {
  id: "markAlertRead",
  description: "Marca una alerta como leída.",
  schema: {
    type: "object",
    properties: { restaurantId: { type: "string" }, alertId: { type: "string" } },
    required: ["restaurantId", "alertId"],
  },
  async run(args, ctx) {
    if (!ctx.accessToken) return { ok: false, error: "Sesión no disponible." };
    const restaurantId = String(args.restaurantId ?? "");
    const alertId = String(args.alertId ?? "");
    if (!restaurantId || !alertId) return { ok: false, error: "Faltan IDs." };
    return withFollow(ctx, {
      intro: "Marcando alerta…",
      successMsg: "✓ Alerta marcada",
      restaurantId,
      targetTab: "alertas",
      targetTabLabel: "Alertas",
      run: async () => {
        try {
          await apiWithAuth.patch(
            `/restaurants/${restaurantId}/alerts/${alertId}/read`,
            {},
            ctx.accessToken,
          );
          return { ok: true as const, result: { alertId }, humanSummary: "Alerta marcada como leída." };
        } catch (err: any) {
          return { ok: false as const, error: err?.response?.data?.message ?? "Error." };
        }
      },
    });
  },
};

export const markAllAlertsReadSkill: Skill = {
  id: "markAllAlertsRead",
  description: "Marca TODAS las alertas no leídas del restaurante como leídas.",
  schema: {
    type: "object",
    properties: { restaurantId: { type: "string" } },
    required: ["restaurantId"],
  },
  async run(args, ctx) {
    if (!ctx.accessToken) return { ok: false, error: "Sesión no disponible." };
    const restaurantId = String(args.restaurantId ?? "");
    if (!restaurantId) return { ok: false, error: "Falta restaurantId." };

    const ok = await ctx.confirmation.confirm({
      title: "Marcar todas como leídas",
      description: "Todas las alertas no leídas se marcarán como leídas.",
      confirmLabel: "Marcar todas",
    });
    if (!ok) return { ok: false, error: "Acción cancelada por el usuario." };

    return withFollow(ctx, {
      intro: "Marcando todas las alertas…",
      successMsg: "✓ Todas marcadas",
      restaurantId,
      targetTab: "alertas",
      targetTabLabel: "Alertas",
      run: async () => {
        try {
          const { data } = await apiWithAuth.patch(
            `/restaurants/${restaurantId}/alerts/read-all`,
            {},
            ctx.accessToken,
          );
          return {
            ok: true as const,
            result: { count: data?.count ?? 0 },
            humanSummary: `${data?.count ?? 0} alerta(s) marcada(s).`,
          };
        } catch (err: any) {
          return { ok: false as const, error: err?.response?.data?.message ?? "Error." };
        }
      },
    });
  },
};
